import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Plus, Building2, Settings, FileText, AlertTriangle, DollarSign, Shield, Calendar } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

export default function OrgSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [states, setStates] = useState([]);
  const [settingsForm, setSettingsForm] = useState({});
  const [branchForm, setBranchForm] = useState({ code: '', name: '', state_id: '' });
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [sRes, bRes, stRes] = await Promise.all([
        api.get('/org/settings'), api.get('/org/branches'), api.get('/org/states'),
      ]);
      setSettings(sRes.data.data); setSettingsForm(sRes.data.data || {});
      setBranches(bRes.data.data || []); setStates(stRes.data.data || []);
    } catch (e) { toast.error('Failed to load settings'); }
  };

  useEffect(() => { fetchData(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/org/settings', settingsForm);
      toast.success('Settings saved');
      fetchData();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const createBranch = async (e) => {
    e.preventDefault();
    try {
      await api.post('/org/branches', branchForm);
      toast.success('Branch created');
      setBranchDialogOpen(false); setBranchForm({ code: '', name: '', state_id: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Organisation Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your organisation details and branches</p>
      </div>

      {/* Org Settings */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> General Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Organisation Name</Label><Input value={settingsForm.org_name || ''} onChange={(e) => setSettingsForm({...settingsForm, org_name: e.target.value})} data-testid="org-name-input" /></div>
            <div className="space-y-2"><Label>ABN</Label><Input value={settingsForm.abn || ''} onChange={(e) => setSettingsForm({...settingsForm, abn: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>NDIS Provider Number</Label><Input value={settingsForm.ndis_provider_number || ''} onChange={(e) => setSettingsForm({...settingsForm, ndis_provider_number: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Timezone</Label><Input value={settingsForm.timezone || ''} onChange={(e) => setSettingsForm({...settingsForm, timezone: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Phone</Label><Input value={settingsForm.phone || ''} onChange={(e) => setSettingsForm({...settingsForm, phone: e.target.value})} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={settingsForm.email || ''} onChange={(e) => setSettingsForm({...settingsForm, email: e.target.value})} /></div>
          </div>
          <div className="space-y-2"><Label>Address</Label><Input value={settingsForm.address || ''} onChange={(e) => setSettingsForm({...settingsForm, address: e.target.value})} /></div>
          <Button onClick={saveSettings} disabled={saving} data-testid="org-save-button">{saving ? 'Saving...' : 'Save Settings'}</Button>
        </CardContent>
      </Card>

      {/* Progress Note Rules */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-4 w-4" /> Progress Note Rules</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 text-sm">
            <Checkbox checked={settingsForm.require_progress_note_before_timesheet_submit || false}
              onCheckedChange={(v) => setSettingsForm({...settingsForm, require_progress_note_before_timesheet_submit: !!v})} />
            Require progress note before timesheet submit
          </label>
          <label className="flex items-center gap-3 text-sm">
            <Checkbox checked={settingsForm.require_progress_note_before_checkout || false}
              onCheckedChange={(v) => setSettingsForm({...settingsForm, require_progress_note_before_checkout: !!v})} />
            Require progress note before check-out (optional)
          </label>
          <Button onClick={saveSettings} disabled={saving} size="sm">{saving ? 'Saving...' : 'Save'}</Button>
        </CardContent>
      </Card>

      {/* Cancellation Rules */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Cancellation Rules</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Late Cancel Window (hours)</Label>
              <Input type="number" value={settingsForm.cancellation_rules?.late_cancel_window_hours ?? 24}
                onChange={(e) => setSettingsForm({...settingsForm, cancellation_rules: {...(settingsForm.cancellation_rules||{}), late_cancel_window_hours: parseInt(e.target.value)}})} />
            </div>
            <div className="space-y-2"><Label>Late Cancel Charge (%)</Label>
              <Input type="number" min="0" max="100" value={settingsForm.cancellation_rules?.charge_late_cancel_percent ?? 100}
                onChange={(e) => setSettingsForm({...settingsForm, cancellation_rules: {...(settingsForm.cancellation_rules||{}), charge_late_cancel_percent: parseInt(e.target.value)}})} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>No-Show Charge (%)</Label>
              <Input type="number" min="0" max="100" value={settingsForm.cancellation_rules?.charge_no_show_percent ?? 100}
                onChange={(e) => setSettingsForm({...settingsForm, cancellation_rules: {...(settingsForm.cancellation_rules||{}), charge_no_show_percent: parseInt(e.target.value)}})} />
            </div>
            <div className="space-y-2 flex items-end pb-2">
              <label className="flex items-center gap-3 text-sm">
                <Checkbox checked={settingsForm.cancellation_rules?.require_evidence_for_chargeable_cancel || false}
                  onCheckedChange={(v) => setSettingsForm({...settingsForm, cancellation_rules: {...(settingsForm.cancellation_rules||{}), require_evidence_for_chargeable_cancel: !!v}})} />
                Require evidence for chargeable cancellation
              </label>
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} size="sm">{saving ? 'Saving...' : 'Save'}</Button>
        </CardContent>
      </Card>

      {/* Claiming Settings */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Claiming Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Export Format</Label>
              <Input value={settingsForm.claiming?.claim_export_format ?? 'GENERIC_CSV_V1'}
                onChange={(e) => setSettingsForm({...settingsForm, claiming: {...(settingsForm.claiming||{}), claim_export_format: e.target.value}})} />
            </div>
            <div className="space-y-2"><Label>Claim Reference Prefix</Label>
              <Input value={settingsForm.claiming?.claim_reference_prefix ?? 'CLM'}
                onChange={(e) => setSettingsForm({...settingsForm, claiming: {...(settingsForm.claiming||{}), claim_reference_prefix: e.target.value}})} className="font-mono" />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} size="sm">{saving ? 'Saving...' : 'Save'}</Button>
        </CardContent>
      </Card>

      {/* Compliance Rules (Day-5) */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-4 w-4" /> Compliance Rules</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Expiry Warning Days</Label>
              <Input type="number" value={settingsForm.compliance?.expiry_warning_days ?? 30}
                onChange={(e) => setSettingsForm({...settingsForm, compliance: {...(settingsForm.compliance||{}), expiry_warning_days: parseInt(e.target.value)}})} />
            </div>
            <div className="space-y-2 flex items-end pb-2">
              <label className="flex items-center gap-3 text-sm">
                <Checkbox checked={settingsForm.compliance?.block_assignment_if_unverified ?? true}
                  onCheckedChange={(v) => setSettingsForm({...settingsForm, compliance: {...(settingsForm.compliance||{}), block_assignment_if_unverified: !!v}})} />
                Block assignment if unverified
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Mandatory Checks</Label>
            <p className="text-xs text-muted-foreground">{(settingsForm.compliance?.mandatory_checks || []).join(', ') || 'None set'}</p>
          </div>
          <Button onClick={saveSettings} disabled={saving} size="sm">{saving ? 'Saving...' : 'Save'}</Button>
        </CardContent>
      </Card>

      {/* Rostering Rules (Day-5) */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="h-4 w-4" /> Rostering Rules</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 text-sm">
            <Checkbox checked={settingsForm.rostering?.enforce_availability ?? true}
              onCheckedChange={(v) => setSettingsForm({...settingsForm, rostering: {...(settingsForm.rostering||{}), enforce_availability: !!v}})} />
            Enforce staff availability when assigning shifts
          </label>
          <label className="flex items-center gap-3 text-sm">
            <Checkbox checked={settingsForm.rostering?.enforce_leave_blocks ?? true}
              onCheckedChange={(v) => setSettingsForm({...settingsForm, rostering: {...(settingsForm.rostering||{}), enforce_leave_blocks: !!v}})} />
            Block shift assignment during approved leave
          </label>
          <Button onClick={saveSettings} disabled={saving} size="sm">{saving ? 'Saving...' : 'Save'}</Button>
        </CardContent>
      </Card>

      <Separator />

      {/* Branches */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2"><Building2 className="h-5 w-5" /> Branches</h2>
        <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
          <DialogTrigger asChild><Button data-testid="branch-create-button"><Plus className="h-4 w-4 mr-2" /> Add Branch</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Branch</DialogTitle><DialogDescription>Add a new branch location</DialogDescription></DialogHeader>
            <form onSubmit={createBranch} className="space-y-4">
              <div className="space-y-2"><Label>Branch Code</Label><Input value={branchForm.code} onChange={(e) => setBranchForm({...branchForm, code: e.target.value})} required placeholder="e.g. SYD-001" className="font-mono" data-testid="branch-code-input" /></div>
              <div className="space-y-2"><Label>Branch Name</Label><Input value={branchForm.name} onChange={(e) => setBranchForm({...branchForm, name: e.target.value})} required data-testid="branch-name-input" /></div>
              <div className="space-y-2"><Label>State</Label>
                <Select value={branchForm.state_id} onValueChange={(v) => setBranchForm({...branchForm, state_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{states.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="branch-submit-button">Create Branch</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>State</TableHead></TableRow></TableHeader>
            <TableBody>
              {branches.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No branches</TableCell></TableRow>
              : branches.map(b => (
                <TableRow key={b.id || b.code} className="hover:bg-muted/60">
                  <TableCell className="font-mono text-sm">{b.code}</TableCell>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell>{b.state_id || 'All'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* States (read-only) */}
      <h2 className="text-xl font-bold">States / Territories</h2>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead></TableRow></TableHeader>
            <TableBody>
              {states.map(s => (
                <TableRow key={s.code}><TableCell className="font-mono">{s.code}</TableCell><TableCell>{s.name}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
